import { useRef, useState } from "react"
import { Link, useNavigate, useParams } from "@tanstack/react-router"
import { IconEnvelopeFill18 } from "nucleo-ui-fill-18"
import {
	IconChevronRightOutline18 as ChevronRight,
	IconArrowDownToLineOutline18 as DownloadIcon,
	IconTrashOutline18 as TrashIcon,
} from "nucleo-ui-outline-18"

import { SENDER_NAME_MAX_LENGTH } from "@productlane/api/src/lib/constants"
import { AUTO_REPLY_PLACEHOLDERS } from "@productlane/lib/src/auto-reply-template"
import {
	AlertDialog,
	Button,
	CopyButton,
	Dialog,
	Input,
	Label,
	Loader,
	Switch,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { EmailEditor } from "@/components/editor/email-editor"
import { CloseLoopPlaceholderSuggestion } from "@/components/editor/extensions/close-loop-placeholders"
import { MetaInfo } from "@/components/meta-info"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { SettingsHeader } from "../../settings-header"
import { HowItWorksButton } from "../../settings-switches/how-it-works-button"
import { SettingsContainer } from "../../settings-switches/settings-container"
import { SettingsSection } from "../../settings-switches/settings-section"
import { DnsRecordBlock } from "../dns-record-block"
import { downloadDnsRecords } from "../download-dns-records"
import { SENDER_NAME_DESCRIPTION, SenderNameInput } from "./sender-name-input"

const FORWARDING_DOCS_ARTICLE = "6c07a98b-6728-4f77-b0a8-4ce1525175ba"

const DEFAULT_AUTO_REPLY_MESSAGE =
	"<p>Thank you for reaching out. We have received your message and will get back to you as soon as possible.</p>"

const AUTO_REPLY_EXTENSIONS = [
	CloseLoopPlaceholderSuggestion.configure({
		placeholders: AUTO_REPLY_PLACEHOLDERS,
	}),
]

function SettingRow({
	label,
	description,
	children,
}: {
	label: string
	description?: string
	children: React.ReactNode
}) {
	return (
		<div className="flex min-h-9 items-center justify-between gap-4">
			<div>
				<div className="text-sm font-medium">{label}</div>
				{description && (
					<div className="mt-0.5 text-xs font-medium text-secondary">
						{description}
					</div>
				)}
			</div>
			{children}
		</div>
	)
}

export const EmailAddressSettingsPage = () => {
	const { addressId } = useParams({ strict: false })
	const navigate = useNavigate()
	const zero = useZero()
	const utils = api.useUtils()
	const workspace = useZeroWorkspace()

	const [autoReplyOpen, setAutoReplyOpen] = useState(false)
	const [removeOpen, setRemoveOpen] = useState(false)
	const [savingAutoReply, setSavingAutoReply] = useState(false)
	const autoReplyRef = useRef<string | null>(null)
	const [autoReplySenderName, setAutoReplySenderName] = useState("")

	const verifyMutation = api.mail.verifyEmailDomain.useMutation({
		onSuccess: async () => {
			await utils.workspace.info.invalidate()
			toast.success({
				title: "Domain verified",
				description: "The address is live.",
				icon: IconEnvelopeFill18,
			})
		},
		onError: (error) => {
			toast.error({ title: "DNS is not live yet", description: error.message })
		},
	})

	const broadcastMutation = api.mail.updateEmailAddress.useMutation({
		onSuccess: async () => {
			await utils.workspace.info.invalidate()
		},
		onError: (error) => {
			toast.error({
				title: "Could not save the setting",
				description: error.message,
			})
		},
	})

	const deleteMutation = api.mail.deleteEmailAddress.useMutation({
		onSuccess: async () => {
			await Promise.all([
				utils.mail.emailAddressLimit.invalidate(),
				utils.workspace.info.invalidate(),
			])
			void navigate({ to: "/settings/emails" })
		},
		onError: (error) => {
			toast.error({
				title: "Could not remove the address",
				description: error.message,
			})
		},
	})

	if (!workspace) return <Loader />

	const address = (workspace.emailSettings ?? []).find(
		(row) => row.id === addressId,
	)
	if (!address) {
		return (
			<>
				<SettingsHeader title="Address not found" />
				<Link
					to="/settings/emails"
					className="text-sm font-medium text-secondary hover:text-primary"
				>
					Back to shared email
				</Link>
			</>
		)
	}

	const isDnsVerified = address.isTxtDnsActive && address.isCnameDnsActive

	const hasOfficeHours = (workspace.officeHours?.length ?? 0) > 0

	const agentAnswersEmail = Boolean(
		workspace.aiChatSettings?.isEnabled &&
		workspace.aiChatSettings.aiAutoRepliesEnabled,
	)

	const update = (
		args: Partial<{
			autoReplyEnabled: boolean
			autoReplyMessage: string
			autoReplySenderName: string | null
			senderName: string | null
			isForwardingSetup: boolean
		}>,
	) =>
		mutateOk(
			zero.mutate(mutators.emailSettings.update({ id: address.id, ...args })),
		).catch(() => toast.error({ title: "Could not save the setting" }))

	const saveAutoReply = async () => {
		setSavingAutoReply(true)
		try {
			await update({
				autoReplyMessage: autoReplyRef.current ?? address.autoReplyMessage,
				autoReplySenderName: autoReplySenderName.trim() || null,
			})
			setAutoReplyOpen(false)
		} finally {
			setSavingAutoReply(false)
		}
	}

	return (
		<>
			<MetaInfo title={address.primaryEmail} />

			<SettingsHeader
				title={address.primaryEmail}
				onTitleClick={() => {
					void navigator.clipboard.writeText(address.primaryEmail)
					toast.success({
						title: "Email address copied",
						icon: IconEnvelopeFill18,
					})
				}}
			/>

			{!isDnsVerified && (
				<>
					<SettingsSection
						title={`Verify ${address.domain}`}
						description="Add these DNS records at your DNS provider. The address activates once both are live."
						className="mb-4 mt-0"
					/>
					<SettingsContainer className="mb-4 space-y-3 divide-y-0">
						<div>
							<DnsRecordBlock
								type="TXT"
								recordKey={address.txtDnsKey}
								value={address.txtDnsValue}
								isActive={address.isTxtDnsActive}
							/>
							<DnsRecordBlock
								type="CNAME"
								recordKey={address.cnameDnsKey}
								value={address.cnameDnsValue}
								isActive={address.isCnameDnsActive}
							/>
						</div>
						<div className="flex items-center gap-2">
							<Button
								variant="outline"
								Icon={DownloadIcon}
								onClick={() => downloadDnsRecords(address)}
							>
								Download for Cloudflare
							</Button>
							<span className="flex-1" />
							<Button
								isLoading={verifyMutation.isLoading}
								onClick={() => verifyMutation.mutate({ id: address.id })}
							>
								Verify DNS records
							</Button>
						</div>
					</SettingsContainer>
				</>
			)}

			{isDnsVerified && (
				<>
					<SettingsSection
						title="Email forwarding"
						description={
							<>
								In your email provider&apos;s settings, forward mail sent to{" "}
								{address.primaryEmail} to the intake address below.{" "}
								<HowItWorksButton
									variant="link"
									label="Docs"
									article={FORWARDING_DOCS_ARTICLE}
								/>
							</>
						}
						className="mb-4 mt-0"
					/>
					<SettingsContainer className="mb-4 space-y-4 divide-y-0">
						<SettingRow
							label="Intake address"
							description="Your provider forwards inbound mail here."
						>
							<span className="flex min-w-0 items-center gap-1">
								<span className="truncate font-mono text-xs text-secondary">
									{address.brandedInboundEmailAddress}
								</span>
								<CopyButton
									value={address.brandedInboundEmailAddress ?? ""}
									className="!text-tertiary hover:!text-primary"
								/>
							</span>
						</SettingRow>
						<SettingRow
							label="Forwarding is set up"
							description="Confirms the rule above exists at your provider."
						>
							<Switch
								name="forwarding"
								label={null}
								checked={address.isForwardingSetup}
								onCheckedChange={(checked) =>
									update({ isForwardingSetup: checked })
								}
							/>
						</SettingRow>
					</SettingsContainer>
				</>
			)}

			<SettingsSection title="Replies" className="mb-4" />
			<SettingsContainer className="mb-4 space-y-2 divide-y-0">
				<SettingRow label="Sender name" description={SENDER_NAME_DESCRIPTION}>
					<SenderNameInput
						key={address.id}
						senderName={address.senderName ?? null}
						workspaceName={workspace.name ?? ""}
						onSave={(senderName) => void update({ senderName })}
					/>
				</SettingRow>
			</SettingsContainer>

			<SettingsSection title="Broadcasts" className="mb-4" />
			<SettingsContainer className="mb-4 space-y-2 divide-y-0">
				<SettingRow
					label="Use for broadcasts"
					description="Changelog broadcasts send from this address by default."
				>
					<Switch
						name="broadcastDefault"
						label={null}
						checked={address.isBroadcastDefault}
						onCheckedChange={(checked) =>
							broadcastMutation.mutate({
								id: address.id,
								isBroadcastDefault: checked,
							})
						}
					/>
				</SettingRow>
			</SettingsContainer>

			<SettingsSection title="Automation" className="mb-4" />
			<SettingsContainer className="mb-4 space-y-4 divide-y-0">
				<SettingRow
					label="AI agent"
					description={
						agentAnswersEmail
							? "The agent answers new messages on this address."
							: "The agent answers email once its Email channel is on."
					}
				>
					<Button
						variant="outline"
						iconRight={ChevronRight}
						onClick={() => void navigate({ to: "/settings/ai/agent" })}
					>
						Agent settings
					</Button>
				</SettingRow>
				<SettingRow
					label="Auto-reply"
					description="Automatically confirm new messages on this address."
				>
					<Switch
						name="autoReply"
						label={null}
						checked={address.autoReplyEnabled}
						onCheckedChange={(checked) => update({ autoReplyEnabled: checked })}
					/>
				</SettingRow>
				{address.autoReplyEnabled && (
					<Button
						variant="link"
						iconRight={ChevronRight}
						className="-mt-2 self-start"
						onClick={() => {
							setAutoReplySenderName(address.autoReplySenderName ?? "")
							setAutoReplyOpen(true)
						}}
					>
						Edit auto-reply message
					</Button>
				)}
			</SettingsContainer>

			<SettingsSection title="Manage" className="mb-4" />
			{address.isPrimary ? (
				<SettingsContainer className="space-y-2 divide-y-0">
					<SettingRow
						label="Primary address"
						description="Changing it re-runs the domain setup when the new address is on a new domain."
					>
						<Link to="/settings/emails/setup">
							<Button variant="outline">Change primary address</Button>
						</Link>
					</SettingRow>
				</SettingsContainer>
			) : (
				<SettingsContainer className="space-y-2 divide-y-0">
					<SettingRow
						label="Remove address"
						description="Mail forwarded to it stops arriving. Existing threads keep their history."
					>
						<Button
							variant="outline"
							Icon={TrashIcon}
							isLoading={deleteMutation.isLoading}
							onClick={() => setRemoveOpen(true)}
						>
							Remove
						</Button>
					</SettingRow>
				</SettingsContainer>
			)}

			<AlertDialog
				isOpen={removeOpen}
				onOpenChange={setRemoveOpen}
				title={`Remove ${address.primaryEmail}?`}
				description="Mail forwarded to it stops arriving and replies can no longer be sent from it. Existing threads keep their history. You can add the address again later."
				confirmText="Remove address"
				cancelText="Keep address"
				variant="positive"
				onCancel={() => setRemoveOpen(false)}
				onConfirm={() => {
					setRemoveOpen(false)
					deleteMutation.mutate({ id: address.id })
				}}
			/>

			<Dialog
				isOpen={autoReplyOpen}
				onOpenChange={setAutoReplyOpen}
				position="right"
				title="Auto-reply message"
				description={`Sent from ${address.primaryEmail} when a new message arrives.`}
			>
				<div className="flex flex-col gap-4">
					<Input
						name="autoReplySenderName"
						label="Sender name"
						value={autoReplySenderName}
						maxLength={SENDER_NAME_MAX_LENGTH}
						placeholder={address.senderName ?? workspace.name ?? ""}
						onChange={(e) => setAutoReplySenderName(e.target.value)}
						suffix={
							<span className="pointer-events-none text-xs tabular-nums text-tertiary">
								{SENDER_NAME_MAX_LENGTH - autoReplySenderName.length}
							</span>
						}
					/>
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="autoReplyMessage">Message</Label>
						<div
							id="autoReplyMessage"
							className="min-h-[200px] rounded-lg border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-transparent"
						>
							<EmailEditor
								initialContent={
									address.autoReplyMessage || DEFAULT_AUTO_REPLY_MESSAGE
								}
								placeholder="Write your auto-reply message..."
								extensions={hasOfficeHours ? AUTO_REPLY_EXTENSIONS : undefined}
								onUpdate={({ editor }) => {
									autoReplyRef.current = editor.getHTML()
								}}
							/>
						</div>
						{hasOfficeHours && (
							<p className="text-xs font-medium text-secondary">
								Type {"{{"} to add a placeholder.
							</p>
						)}
					</div>
					<Button
						size="sm"
						isLoading={savingAutoReply}
						onClick={saveAutoReply}
						className="min-w-20 self-end"
					>
						Save
					</Button>
				</div>
			</Dialog>
		</>
	)
}
